import { Button, Container, Dialog, Grid, Paper, Stack, Typography, Box } from '@mui/material';
import { FormProvider, SelectTag, TextField } from '../../../../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getCurrentDateTime } from '../../../../../utils';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RegistrationRegisterReport } from '../../../../Print/options/Hospital/HMS/RegistrationRegisterReport';
import { BILLING_TYPE_REPORT } from '../../../../../constants/dropdown';

const validationSchema = Yup.object().shape({
  from_date: Yup.string().required('From date is required'),
  to_date: Yup.string().required('To date is required')
});

export const ServiceRegisterReport = () => {
  const { pathname } = useLocation();
  const [openPrint, setOpenPrint] = useState(false);
  const [groupDropdown, setGroupDropdown] = useState([]);
  const [subGroupDropdown, setSubGroupDropdown] = useState([]);
  const [serviceDropdown, setServiceDropdown] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date().toISOString().split('T')[0],
      group: '',
      subgroup: '',
      service: ''
    },
    mode: 'all'
  });

  const { watch } = methods;

  const getServiceDropdownData = useCallback(async () => {
    const data = await dropdownFetch('service');
    setServiceDropdown(data);
  }, []);

  const getGroupDropdownData = useCallback(async () => {
    const data = await dropdownFetch('group-master');
    setGroupDropdown(data);
  }, []);

  const getSubGroupDropdownData = useCallback(async () => {
    const data = await dropdownFetch('subgroup-master');
    setSubGroupDropdown(data);
  }, []);

  useEffect(() => {
    getServiceDropdownData();
    getGroupDropdownData();
    getSubGroupDropdownData();
  }, [getServiceDropdownData, getGroupDropdownData, getSubGroupDropdownData]);

  // Watch form data to reactively handle filtering
  const group = watch('group');
  const subgroup = watch('subgroup');
  const service = watch('service');

  // Filter subgroups based on the selected group
  const filteredSubgroups = subGroupDropdown.filter((subgroupData) => subgroupData.groupname === group);

  // Filter services based on the selected group and subgroup
  const filteredServices = serviceDropdown.filter(
    (serviceData) => serviceData.main_group === group && serviceData.sub_group === subgroup
  );

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Service Register Report
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <Button
                  variant="contained"
                  disabled={!watch('to_date') || !watch('to_date')}
                  onClick={() =>
                    window.open(
                      `/print${pathname}?refId=${JSON.stringify({
                        from_date: watch('from_date'),
                        to_date: watch('to_date'),
                        group: watch('group'),
                        subgroup: watch('subgroup'),
                        service: watch('service'),
                        billing_type: watch('billing_type')
                      })}`,
                      '_blank'
                    )
                  }
                >
                  View Report
                </Button>
                <Button variant="contained">Exit</Button>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '60%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={11} sx={{ display: 'flex', gap: 0.3, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>From Date</Typography>
                    <TextField sx={{ flex: 0.4 }} name="from_date" type="date" />
                    <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>To Date</Typography>
                    <TextField sx={{ flex: 0.4 }} name="to_date" type="date" />
                  </Stack>
                  {/* <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Group</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="group"
                      menus={groupDropdown.map((data) => ({ 
                        ...data, name: data.groupname }))}
                    />
                  </Stack> */}
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Group</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="group"
                      menus={[{ name: 'All', _id: 'all' }, ...groupDropdown.map((data) => ({
                        ...data, name: data.groupname
                      }))]} // Add "All" option at the start
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Sub Group</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="subgroup"
                      menus={filteredSubgroups.map((data) => ({ ...data, name: data.subgroupname }))}
                      disabled={!group}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Billing Type</Typography>
                    <SelectTag formControlSX={{ flex: 0.8 }} name="billing_type" menus={BILLING_TYPE_REPORT} />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Service</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="service"
                      menus={[
                        { _id: 'all', name: 'All' }, // Add the "All" option first
                        ...filteredServices.map((data) => ({ ...data, name: data.service_name })) // Map over filteredServices
                      ]}
                      disabled={!subgroup}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
    </>
  );
};

export default ServiceRegisterReport;
